import React, { Fragment } from 'react';
import classNames from 'classnames';
import format from 'date-fns/format';
import { ErrorMessage } from 'formik';
import addMinutes from 'date-fns/add_minutes';
import Button from '@material-ui/core/Button';
import ButtonBase from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardMedia from '@material-ui/core/CardMedia';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormLabel from '@material-ui/core/FormLabel';
import Grow from '@material-ui/core/Grow';
import IconButton from '@material-ui/core/IconButton';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Switch from '@material-ui/core/Switch';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import ClearIcon from '@material-ui/icons/Clear';
import { withStyles } from '@material-ui/core/styles';
import ListDialog from 'components/ListDialog';
import ReactSelect from 'components/ReactSelect';
import LocationDialog from './LocationDialog';

const blobUrls = new WeakMap();

const blobUrl = blob => {
  if (blobUrls.has(blob)) {
    return blobUrls.get(blob);
  } else {
    let url = URL.createObjectURL(blob);
    blobUrls.set(blob, url);
    return url;
  }
};

const days = {
  // Using values from https://schema.org/openingHours
  Mo: { label: 'Monday', shortLabel: 'Mo' },
  Tu: { label: 'Tuesday', shortLabel: 'Tu' },
  We: { label: 'Wednesday', shortLabel: 'We' },
  Th: { label: 'Thursday', shortLabel: 'Th' },
  Fr: { label: 'Friday', shortLabel: 'Fr' },
  Sa: { label: 'Saturday', shortLabel: 'Sa' },
  Su: { label: 'Sunday', shortLabel: 'Su' },
};

const hours = (() => {
  let date = new Date(0);
  date = addMinutes(date, date.getTimezoneOffset());

  return [...Array(2 * 24).keys()].reduce(acc => {
    acc[format(date, 'HH:mm')] = format(date, 'hh:mm a');
    date = addMinutes(date, 30);
    return acc;
  }, {});
})();

const tipsPlaceholders = [
  'Expect a 45 min waiting post 7:30 PM.',
  'Do not miss the filter coffee. It is outstanding.',
  'Ask for the Bruschetta even though it\u2019s not on the menu anymore.',
];

const dialogContent = {
  helperTextImage: {
    dialogTitle: 'Tips for Images',
    dialogItems: [
      'Pick images that bring the food alive in the context that it is being mentioned. Ideally try to find images that are of dishes at the specific outlet you are writing about. Neat, well-composed close-ups of the dishes look great.',
      'Do share at least one shot of the location to set context. For example, if you are writing about Chandni Chowk, pick an image that shows the streets, the people, and the area.',
      'Caption: A caption will help the reader get context so do add it.',
      'Credits: Do make a note of the source of the image and add it in the Pic credit section.',
      'Image size: The ideal image size is 100-200KB. Larger images will be downsized automatically.',
    ],
  },
  helperTextDescription: {
    dialogTitle: 'Writing Guidelines',
    dialogItems: [
      'What makes an eatery irresistible to us is the story. Every place has a beginning that started as an idea, that shows us how the owners envisioned, how they come about creating this lovely food. Spin us that tale and you’ll have our attention always.',
      'What does the place look like? Is the small, big, rustic, posh, charming, themed? Paint us a picture with your words, or with an actual picture.',
      'Does the ambiance set the mood? Some light music, live music, dim light, bright light, flashy lights? Tell us what we should expect.',
      'Let’s not forget the food! How does it look? Does it make you drool? In what did they serve it? What was that first bite like? Did the flavours explode in your mouth? Let us live vicariously through that adventure of yours.',
      'Did the food blow your mind away? Tell us more, what about it was outstanding? What did you taste? Sweet, sour, crunchy, umami? Describe it for us.',
    ],
  },
};

const styles = theme => ({
  cardMedia: {
    height: 108,
  },
  dayPicker: {
    flexBasis: 60,
    maxWidth: 200,
  },
  helperTextButton: {
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    minHeight: 'inherit',
    padding: 0,
  },
  timeControl: {
    minWidth: 105,
  },
  tipsListItem: {
    paddingRight: 48,
  },
});

const DeleteButton = props => (
  <IconButton
    className="absolute pin-r pin-t p-1 m-2 bg-err-light text-white border shadow-md"
    aria-label="Remove"
    title="Delete"
    {...props}
  >
    <ClearIcon />
  </IconButton>
);

class Editor extends React.Component {
  state = {
    dialogOpen: false,
    dialogTitle: '',
    dialogItems: [],
    locationDialogOpen: false,
    featuredImageIn: true,
  };

  constructor(props) {
    super(props);
    this.locationRef = React.createRef();
  }

  handleDialogButtonClick = ({ dialogTitle, dialogItems }) => {
    this.setState({ dialogTitle, dialogItems, dialogOpen: true });
  };

  handleDialogClose = () => {
    this.setState({ dialogOpen: false });
  };

  handleLocationDialogChange = location =>
    this.props.setFieldValue('location', location);

  handleLocationDialogClose = () => {
    this.setState({ locationDialogOpen: false });
  };

  handleLocationFocus = e => {
    this.locationRef.current.blur();
    this.setState({ locationDialogOpen: true });
  };

  render() {
    const {
      classes,
      errors,
      handleChange,
      handleBlur,
      setFieldValue,
      touched,
      values,
      derived: { amenities, cities, cuisines, dishTags },
    } = this.props;

    return (
      <Fragment>
        <Typography
          align="center"
          color="textPrimary"
          component="h2"
          gutterBottom
          variant="h4"
        >
          {values.id ? 'Edit eatery' : 'Add eatery'}
        </Typography>
        <TextField
          autoComplete="off"
          fullWidth
          helperText="Use the name that you see on the eatery signboard"
          inputProps={{ maxLength: 250 }}
          label="Eatery name"
          margin="dense"
          name="name"
          onBlur={handleBlur}
          onChange={handleChange}
          placeholder="Billoo's Tandoor"
          required
          spellCheck="false"
          value={values.name}
        />
        <ReactSelect
          className="w-full pt-4"
          error={touched.city && !!errors.city}
          formik={{ handleBlur, setFieldValue }}
          helperText="Pick the city the eatery is located in."
          label="City"
          margin="normal"
          required
          selectProps={{
            name: 'city',
            options: cities,
            value: values.city,
          }}
        />
        <TextField
          autoComplete="street-address"
          disabled={values.name.length === 0 || values.city.length === 0}
          fullWidth
          helperText="Drop the pin on the exact location, we'll do the rest."
          inputProps={{ maxLength: 250 }}
          label="Location"
          margin="dense"
          multiline
          name="location.fullAddress"
          onBlur={handleBlur}
          onFocus={this.handleLocationFocus}
          inputRef={this.locationRef}
          required
          rows={1}
          spellCheck="false"
          value={values.location.fullAddress}
        />
        <TextField
          autoComplete="off"
          fullWidth
          helperText="A highlight that sets this eatery apart, in one crisp line."
          inputProps={{ maxLength: 70 }}
          label="Summary"
          margin="dense"
          name="summary"
          onBlur={handleBlur}
          onChange={handleChange}
          placeholder="A cozy place to enjoy coffee with friends."
          required
          value={values.summary}
        />
        <ReactSelect
          className="w-full pt-4"
          error={touched.cuisines && !!errors.cuisines}
          formik={{ handleBlur, setFieldValue }}
          helperText="Pick all the cuisines the establishment should be visited for."
          label="Cuisines"
          margin="normal"
          required
          selectProps={{
            isMulti: true,
            name: 'cuisines',
            options: cuisines,
            value: values.cuisines,
          }}
        />
        <ReactSelect
          className="w-full pt-4"
          error={touched.mustTryDishes && !!errors.mustTryDishes}
          formik={{ handleBlur, setFieldValue }}
          createable
          helperText="Which dishes do you absolutely recommend?"
          label="Must try dishes"
          margin="normal"
          required
          selectProps={{
            isMulti: true,
            name: 'mustTryDishes',
            options: dishTags,
            value: values.mustTryDishes,
          }}
        />
        <ReactSelect
          className="w-full pt-4"
          createable
          formik={{ handleBlur, setFieldValue }}
          helperText="Pick a few more highlights from the menu that you suggest."
          label="Also serves"
          margin="normal"
          selectProps={{
            isMulti: true,
            name: 'alsoServes',
            options: dishTags,
            value: values.alsoServes,
          }}
        />
        <FormControl
          className="w-full"
          error={touched.featuredImage && !!errors.featuredImage}
          margin="normal"
          required
        >
          <FormLabel component="legend">Featured image</FormLabel>
          <div className="flex flex-wrap -mx-2">
            {values.featuredImage && (
              <Grow
                in={this.state.featuredImageIn}
                style={{ transformOrigin: '50% 0 0' }}
                onExited={() => setFieldValue('featuredImage', null)}
              >
                <div className="basis-100 sm-basis-50 p-2">
                  <Card className="relative">
                    <CardMedia
                      className={classes.cardMedia}
                      image={
                        values.featuredImage.uri ||
                        blobUrl(values.featuredImage.file)
                      }
                    />
                    <DeleteButton
                      onClick={() => {
                        this.setState({ featuredImageIn: false });
                      }}
                    />
                    <CardActions className="flex-col items-stretch px-2">
                      <TextField
                        className="my-1"
                        autoComplete="off"
                        inputProps={{ maxLength: 140 }}
                        label="Caption"
                        name="featuredImage.description"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        placeholder="Traditionally themed interior."
                        // required={!values.featuredImage.uri}
                        value={values.featuredImage.description}
                      />
                      <TextField
                        className="my-1"
                        autoComplete="name"
                        inputProps={{ maxLength: 60 }}
                        label="Photo credit"
                        name="featuredImage.picCredits"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        // required={!values.featuredImage.uri}
                        spellCheck="false"
                        value={values.featuredImage.picCredits}
                      />
                    </CardActions>
                  </Card>
                </div>
              </Grow>
            )}
          </div>
          <div className="mt-4">
            <input
              accept=".jpg,.jpeg,image/jpeg"
              className="hidden"
              id="featured-file"
              name="images"
              onChange={event => {
                setFieldValue('featuredImage', {
                  file: event.currentTarget.files[0],
                  description: '',
                  picCredits: '',
                });
                this.setState({ featuredImageIn: true });
              }}
              type="file"
            />
            <label htmlFor="featured-file">
              <Button variant="contained" component="span">
                Attach image
              </Button>
            </label>
          </div>
          <FormHelperText className="mt-3">
            Share a featured image.&nbsp;
            <ButtonBase
              className={classes.helperTextButton}
              component="span"
              onClick={() =>
                this.handleDialogButtonClick(dialogContent.helperTextImage)
              }
              size="small"
            >
              Click here for some tips.
            </ButtonBase>
          </FormHelperText>
        </FormControl>
        <FormControl
          className="w-full"
          error={touched.eateryImages && !!errors.eateryImages}
          margin="normal"
        >
          <FormLabel component="legend">Eatery Images</FormLabel>
          <div className="flex flex-wrap -mx-2">
            {values.eateryImages.map((item, index) => (
              <div
                key={item.id || item.file.name}
                className="basis-100 sm-basis-50 p-2"
              >
                <Card className="relative">
                  <CardMedia
                    className={classes.cardMedia}
                    image={item.uri || blobUrl(item.file)}
                  />
                  <DeleteButton
                    onClick={() => {
                      setFieldValue(
                        'eateryImages',
                        values.eateryImages.filter((_, i) => i !== index)
                      );
                    }}
                  />
                  <CardActions className="flex-col items-stretch px-2">
                    <TextField
                      className="my-1"
                      autoComplete="off"
                      inputProps={{ maxLength: 140 }}
                      label="Caption"
                      name={`eateryImages[${index}].description`}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      placeholder="Traditionally themed interior."
                      // required={!item.uri}
                      value={values.eateryImages[index].description}
                    />
                    <TextField
                      className="my-1"
                      autoComplete="name"
                      inputProps={{ maxLength: 60 }}
                      label="Photo credit"
                      name={`eateryImages[${index}].picCredits`}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      // required={!item.uri}
                      spellCheck="false"
                      value={values.eateryImages[index].picCredits}
                    />
                  </CardActions>
                </Card>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <input
              accept=".jpg,.jpeg,image/jpeg"
              className="hidden"
              id="images-file"
              multiple
              name="images"
              onChange={event =>
                setFieldValue(
                  'eateryImages',
                  values.eateryImages.concat(
                    Array.prototype.map.call(
                      event.currentTarget.files,
                      file => ({
                        file,
                        description: '',
                        picCredits: '',
                      })
                    )
                  )
                )
              }
              type="file"
            />
            <label htmlFor="images-file">
              <Button variant="contained" component="span">
                Attach images
              </Button>
            </label>
          </div>
          <FormHelperText className="mt-3">
            Share a few images of the food and ambiance.&nbsp;
            <ButtonBase
              className={classes.helperTextButton}
              component="span"
              onClick={() =>
                this.handleDialogButtonClick(dialogContent.helperTextImage)
              }
              size="small"
            >
              Click here for some tips.
            </ButtonBase>
          </FormHelperText>
        </FormControl>
        <FormControl className="w-full" margin="normal">
          <FormLabel component="legend">Menu Images</FormLabel>
          <div className="flex flex-wrap -mx-2">
            {values.menuImages.map((item, index) => (
              <div
                key={item.uri || item.file.name}
                className="basis-100 sm-basis-50 p-2"
              >
                <Card className="relative">
                  <CardMedia
                    className={classes.cardMedia}
                    image={item.uri || blobUrl(item.file)}
                  />
                  <DeleteButton
                    onClick={() => {
                      setFieldValue(
                        'menuImages',
                        values.menuImages.filter((_, i) => i !== index)
                      );
                    }}
                  />
                </Card>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <input
              accept=".jpg,.jpeg,image/jpeg"
              className="hidden"
              id="menu-file"
              multiple
              name="images"
              onChange={event =>
                setFieldValue(
                  'menuImages',
                  values.menuImages.concat(
                    Array.prototype.map.call(
                      event.currentTarget.files,
                      file => ({
                        file,
                        description: '',
                        picCredits: '',
                      })
                    )
                  )
                )
              }
              type="file"
            />
            <label htmlFor="menu-file">
              <Button variant="contained" component="span">
                Attach images
              </Button>
            </label>
          </div>
          <FormHelperText className="mt-3">
            Share images of the menu, if possible.
          </FormHelperText>
        </FormControl>
        <TextField
          autoComplete="off"
          fullWidth
          helperText={
            <Fragment>
              Use our writing guidelines.&nbsp;
              <ButtonBase
                className={classes.helperTextButton}
                component="span"
                onClick={() =>
                  this.handleDialogButtonClick(
                    dialogContent.helperTextDescription
                  )
                }
                size="small"
              >
                Click here.
              </ButtonBase>
            </Fragment>
          }
          inputProps={{ maxLength: 3000 }}
          label="Description"
          margin="normal"
          multiline
          name="description"
          onBlur={handleBlur}
          onChange={handleChange}
          placeholder=""
          rows={4}
          rowsMax={5}
          value={values.description}
        />
        <FormControl
          className="w-full items-start"
          error={touched.tips && !!errors.tips}
          margin="normal"
          required
        >
          <FormLabel component="legend">Pundit Tips</FormLabel>
          <FormHelperText>
            Useful tips that would enhance a visitor's experience.
          </FormHelperText>
          <List className="w-full">
            {values.tips.map(({ id, tip }, index) => (
              <ListItem
                key={id}
                className={classes.tipsListItem}
                disableGutters
              >
                <TextField
                  autoComplete="off"
                  fullWidth
                  inputProps={{ maxLength: 250 }}
                  multiline
                  name={`tips[${index}].tip`}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  placeholder={
                    tipsPlaceholders[index % tipsPlaceholders.length]
                  }
                  rows={1}
                  rowsMax={3}
                  value={tip}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    aria-label="Remove"
                    onClick={() =>
                      setFieldValue(
                        'tips',
                        values.tips.filter((_, i) => i !== index)
                      )
                    }
                  >
                    <ClearIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
          <Button
            size="small"
            onClick={() => {
              const tips = values.tips
                .filter(({ tip }) => tip.trim())
                .concat([
                  {
                    id: `__isNew-${Math.round(Math.random() * 10000)}__`,
                    tip: '',
                  },
                ]);
              setFieldValue('tips', tips);
            }}
          >
            <AddIcon className="mr-2 icon-small" />
            Add tip
          </Button>
          <ErrorMessage
            className="mt-3"
            name="tips"
            component={FormHelperText}
          />
        </FormControl>
        <fieldset className="mx-0 mt-4 px-4">
          <FormLabel className="px-1" component="legend">
            Eatery Information
          </FormLabel>
          <TextField
            autoComplete="tel"
            helperText="Please ensure this is a valid number"
            inputProps={{ maxLength: 15 }}
            label="Phone number"
            margin="normal"
            name="tel"
            onBlur={handleBlur}
            onChange={handleChange}
            type="tel"
            value={values.tel}
          />

          <FormControl
            className="w-full mb-0"
            error={touched.openingHour && !!errors.openingHour}
            margin="normal"
          >
            <FormLabel component="legend">Timings</FormLabel>
          </FormControl>
          {values.openingHour.map((timing, index) => (
            <FormGroup key={index} row>
              <FormControl
                className={classNames('flex-grow', classes.dayPicker)}
                margin="normal"
                // required
              >
                <InputLabel htmlFor="days" shrink>
                  Days
                </InputLabel>
                {/* TODO: sort days in renderValue */}
                {/* TODO: use shortLabel for display in renderValue */}
                <Select
                  autoWidth
                  multiple
                  name={`openingHour[${index}].days`}
                  value={timing.days}
                  input={<Input id="days" />}
                  onChange={handleChange}
                  renderValue={selected => selected.join(', ')}
                >
                  {Object.keys(days).map(key => (
                    <MenuItem key={key} value={key}>
                      <Checkbox checked={timing.days.indexOf(key) > -1} />
                      <ListItemText primary={days[key].label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl
                className={classNames(classes.timeControl, 'mx-4')}
                margin="normal"
                // required
              >
                <InputLabel htmlFor="openingTime" shrink>
                  Opening time
                </InputLabel>
                <Select
                  autoWidth
                  input={<Input id="openingTime" />}
                  name={`openingHour[${index}].openingTime`}
                  onChange={handleChange}
                  value={timing.openingTime}
                >
                  {Object.keys(hours).map(key => (
                    <MenuItem key={key} value={key}>
                      {hours[key]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl
                className={classes.timeControl}
                margin="normal"
                // required
              >
                <InputLabel htmlFor="closingTime" shrink>
                  Closing time
                </InputLabel>
                <Select
                  autoWidth
                  name={`openingHour[${index}].closingTime`}
                  value={timing.closingTime}
                  input={<Input id="closingTime" />}
                  onChange={handleChange}
                >
                  {Object.keys(hours).map(key => (
                    <MenuItem key={key} value={key}>
                      {hours[key]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton
                className="self-end"
                aria-label="Remove"
                onClick={() =>
                  setFieldValue(
                    'openingHour',
                    values.openingHour.filter((_, i) => i !== index)
                  )
                }
              >
                <ClearIcon />
              </IconButton>
            </FormGroup>
          ))}
          <Button
            className="mt-2"
            size="small"
            onClick={() => {
              const openingHour = values.openingHour.concat([
                {
                  days: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
                  openingTime: '',
                  closingTime: '',
                },
              ]);
              setFieldValue('openingHour', openingHour);
            }}
          >
            <AddIcon className="mr-2 icon-small" />
            Add timing
          </Button>
          <ErrorMessage
            error={touched.openingHour && !!errors.openingHour}
            name="openingHour"
            component={FormHelperText}
          />

          <FormControl component="fieldset" margin="normal">
            <FormLabel component="legend">Amenities</FormLabel>
            <FormGroup className="mt-1" row>
              {amenities.map(({ value, label }) => (
                <FormControlLabel
                  key={value}
                  className="basis-50 sm-basis-33 mr-0"
                  control={
                    <Switch
                      checked={values.amenities[label]}
                      onChange={handleChange}
                    />
                  }
                  label={label}
                  name={`amenities.${label}`}
                />
              ))}
            </FormGroup>
          </FormControl>

          <FormControl component="fieldset" margin="normal">
            <FormLabel component="legend">Ambiance</FormLabel>
            <FormGroup className="mt-1" row>
              {Object.keys(values.ambiences).map(id => (
                <FormControlLabel
                  key={id}
                  className="basis-50 sm-basis-33 mr-0"
                  control={
                    <Checkbox
                      checked={values.ambiences[id].value}
                      onChange={handleChange}
                    />
                  }
                  label={values.ambiences[id].label}
                  name={`ambiences.${id}.value`}
                />
              ))}
            </FormGroup>
          </FormControl>
        </fieldset>

        {this.state.dialogOpen && (
          <ListDialog
            open={this.state.dialogOpen}
            onClose={this.handleDialogClose}
            title={this.state.dialogTitle}
            items={this.state.dialogItems}
          />
        )}

        {this.state.locationDialogOpen && (
          <LocationDialog
            onChange={this.handleLocationDialogChange}
            onClose={this.handleLocationDialogClose}
            open={this.state.locationDialogOpen}
            query={`${values.name}, ${values.city[0].label}`}
            value={values.location}
          />
        )}
      </Fragment>
    );
  }
}

export default withStyles(styles)(Editor);
